import {
  AppleDevicesService,
  AppleSoftwareLookupServiceService,
  ApplicationConfiguration,
  AvailableLookupOsUpdate,
  AvailableOSUpdate,
  Certificate,
  ConfigurationProfile,
  InstalledApplication,
  OSUpdateStatus,
} from '@rocksoft/mdm-sdk-v2';

import { AppleDeviceProfileTypeConfig } from './apple-device-profile.types';

/**
 * Factory function that creates profile type configurations.
 * Use this with Angular's DI to provide configs to the store.
 *
 * @example
 * ```typescript
 * {
 *   provide: APPLE_DEVICE_PROFILE_CONFIGS,
 *   useFactory: createAppleDeviceProfileConfigs,
 *   deps: [AppleDevicesService, AppleSoftwareLookupServiceService],
 * }
 * ```
 */
export function createAppleDeviceProfileConfigs(
  appleDevicesService: AppleDevicesService,
  appleLookupService: AppleSoftwareLookupServiceService
): AppleDeviceProfileTypeConfig[] {
  return [
    // Single-item profiles
    {
      profileType: 'device-information',
      apiFn: (udid) => appleDevicesService.getAppleDeviceInformation({ udid, apiVersion: '2.0' }),
      isMultiItem: false,
    },
    {
      profileType: 'security-info',
      apiFn: (udid) => appleDevicesService.getSecurityInfo({ udid, apiVersion: '2.0' }),
      isMultiItem: false,
    },

    // Multi-item profiles
    {
      profileType: 'certificate',
      apiFn: (udid) => appleDevicesService.getCertificates({ udid, apiVersion: '2.0' }),
      isMultiItem: true,
      getItemId: (cert: Certificate) => cert.commonName ?? 'unknown',
    },
    {
      profileType: 'configuration-profile',
      apiFn: (udid) => appleDevicesService.getConfigurationProfiles({ udid, apiVersion: '2.0' }),
      isMultiItem: true,
      getItemId: (profile: ConfigurationProfile) => profile.payloadUUID ?? 'unknown',
    },
    {
      profileType: 'os-update-status',
      apiFn: (udid) => appleDevicesService.getOsUpdateStatuses({ udid, apiVersion: '2.0' }),
      isMultiItem: true,
      getItemId: (status: OSUpdateStatus) => status.productKey ?? 'unknown',
    },
    {
      profileType: 'application-configuration',
      apiFn: (udid) => appleDevicesService.getApplicationConfigurations({ udid, apiVersion: '2.0' }),
      isMultiItem: true,
      getItemId: (config: ApplicationConfiguration) => config.identifier ?? 'unknown',
    },
    {
      profileType: 'mdm-available-os-update',
      apiFn: (udid) => appleDevicesService.getAvailableOsUpdates({ udid, apiVersion: '2.0' }),
      isMultiItem: true,
      getItemId: (update: AvailableOSUpdate) => update.version ?? 'unknown',
    },
    {
      profileType: 'mdm-installed-application',
      apiFn: (udid) => appleDevicesService.getInstalledApplications({ udid, apiVersion: '2.0' }),
      isMultiItem: true,
      getItemId: (app: InstalledApplication) => app.identifier ?? 'unknown',
    },
    {
      profileType: 'available-lookup-os-update',
      apiFn: (udid) => appleLookupService.getAvailableLookupOsUpdatesForDevice({ udid, apiVersion: '2.0' }),
      isMultiItem: true,
      getItemId: (update: AvailableLookupOsUpdate) => update.productVersion ?? 'unknown',
    },
  ];
}
